import { get } from './API';

/**
 * One booking's AI overview — the short generated briefing shown at the top
 * of Order Details.
 *
 * LOCKED CONTRACT — GET /bookings/ai-overview?booking_id=<id | reference>
 *
 * THIS IS THE CACHED READ, AND IT NEVER GENERATES. The endpoint answers
 * `absent` when there is no stored briefing for this booking, fingerprint
 * and reader language, and generation then happens over the AgentCore
 * runtime where it can be streamed (`OverviewStreamService`). So this call
 * is one indexed Mongo read and is fast on every open — which is what lets
 * a cached briefing be painted whole instead of revealed.
 *
 *   200 { status: "absent", overview: null }
 *
 *   200 {
 *     status: "ready",
 *     overview: {
 *       summary: string,                 // prose, a few short paragraphs
 *       handled_by: string | null,
 *       partner: { name, id, kind } | null,
 *       operators: [{ name, id, role }],
 *       sources: [{ kind, label, ref }], // kind: booking|audit|email|document
 *       gaps: string[],                  // what was searched for and NOT found
 *       language: "en" | "ru" | "hu",     // what `summary` is written in
 *       generated_at: ISO8601,
 *       model: string,
 *       fingerprint: string,
 *     }
 *   }
 *
 * Callers fire this and do NOT await it before painting — see
 * `Hooks/useBookingAiOverview`. Nothing on this path may block the booking
 * details the user actually opened the modal for.
 */

/**
 * Lift `{ status, overview }` out of the response.
 *
 * The contract puts it at the top level of the body, so `response.data` IS
 * the pair. Every other endpoint in this app answers inside the house
 * envelope (`{ data: … }`, which is why the rest of Services reads
 * `response.data.data`), so the one nesting is tolerated too rather than
 * showing nothing if the endpoint lands wrapped. Anything else — including
 * the `undefined` a reset jest mock returns — is "no overview", never a throw.
 */
const unwrap = (response) => {
  const body = response?.data;
  if (body?.overview) return body;
  if (body?.data?.overview) return body.data;
  return null;
};

export default {
  /**
   * `bookingId` is a Mongo `_id` OR a `booking_reference` — the endpoint
   * accepts either. Returns `{ status, overview }`, or null when the answer
   * carries no overview.
   */
  getForBooking: async (bookingId) => {
    if (!bookingId) return null;
    const response = await get('bookings/ai-overview', { booking_id: bookingId });
    return unwrap(response);
  },
};
